import React, { useCallback, useEffect, useMemo, useState } from "react";
import { RefreshCw, ArrowLeft, ExternalLink } from "lucide-react";
import { T, GOLD_INK, statusColor, statusFill } from "../theme/tokens.js";
import { APP_NAME } from "../config.js";
import { Panel, Btn, Chip, Kpi, Empty, Notice, Modal, Labelled } from "./ui/index.jsx";
import SiteMap from "./SiteMap.jsx";
import DrillPanel from "./DrillPanel.jsx";
import RowTable from "./RowTable.jsx";
import ColumnsEditor from "./ColumnsEditor.jsx";
import Photographs from "./Photographs.jsx";
import { fetchFeatures, featuresToTable, queryUrl, LAT_KEY, LON_KEY } from "../lib/arcgis.js";
import { findCoordinates, findStatusColumns, findLabelColumn, stateLabel } from "../lib/geo.js";
import { interfaceSummary, parseInterfaceTags, FAMILY_LABEL } from "../lib/icd.js";
import { joinToLayer, keyCandidates, ourColumns, seedRows, OURS } from "../lib/joinRows.js";
import { useTrackers, useTracker } from "../lib/useTrackers.js";
import { supabase, friendlyError } from "../lib/supabase.js";
import { keyFor } from "../lib/importers/spreadsheet.js";

/* A live map layer, read where it is kept.
 *
 * VIDE's off-site program is run out of an ArcGIS layer in the Witt O'Brien's
 * organisation. That copy is the accurate one: whoever holds the GIS marks a
 * school's review complete there and the whole program reads it from there.
 * So this app reads it too, rather than keeping a copy that would disagree
 * within a week.
 *
 * Nothing on this screen can be edited, and that is deliberate rather than
 * unfinished. The layer is edited in ArcGIS, where it lives, and this screen
 * shows what it says. Editing it in two places would put two answers in the
 * world for the same question, which is the problem this was built to avoid.
 */

const RANK_FIELD = /^priority$/i;

function useLayer(source, read) {
  const [state, setState] = useState({ loading: true });

  const load = useCallback(async () => {
    setState((s) => ({ ...s, loading: true, error: null }));
    try {
      const payload = await read(source.url, source.where || "1=1");
      const table = featuresToTable(payload);
      setState({ loading: false, ...table, truncated: Boolean(payload.truncated), at: new Date() });
    } catch (e) {
      setState({ loading: false, error: e.message });
    }
  }, [read, source.url, source.where]);

  useEffect(() => { load(); }, [load]);
  return { ...state, reload: load };
}

/* A count that opens what it counted, drawn as a bar against the largest in
   its own list so the shape of the program reads at a glance. */
function CountBar({ label, count, of, color, onOpen, one, many }) {
  const width = of ? Math.max(3, Math.round((count / of) * 100)) : 0;
  const noun = count === 1 ? one : many;
  return (
    <div onClick={onOpen} title={onOpen ? "Open these" : undefined}
      style={{ marginBottom: 9, cursor: onOpen ? "pointer" : "default" }}>
      <div style={{ display: "flex", justifyContent: "space-between", gap: 12,
                    fontSize: 12.5, marginBottom: 3 }}>
        <span style={{ color: T.ink, minWidth: 0, overflow: "hidden",
                       textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{label}</span>
        <span style={{ color: T.soft, whiteSpace: "nowrap" }}>{count} {noun}</span>
      </div>
      <div style={{ height: 8, background: T.line + "88", borderRadius: 4, overflow: "hidden" }}>
        <div style={{ width: `${width}%`, height: "100%", background: color }} />
      </div>
    </div>
  );
}

/* Starting a tracker of our own against a layer.
 *
 * The columns are typed from scratch, every time, because what Witt O'Brien's
 * needs to record against a school is not something anybody can guess and is
 * not the same on the next program. What is not typed is the rows: there is
 * one per feature in the layer, carrying the key that lines the two up and the
 * feature's name so the table is readable. Typing twenty eight school codes by
 * hand is not a feature.
 */
function StartOurTracker({ layer, source, onClose, onCreate }) {
  const keys = useMemo(
    () => keyCandidates(layer.columns, layer.rows), [layer]);
  const labelCol = useMemo(
    () => findLabelColumn(layer.columns, layer.rows), [layer]);

  const [title, setTitle] = useState(`${source.title}: our tracking`);
  const [keyField, setKeyField] = useState(keys[0]?.key ?? "");
  const [busy, setBusy] = useState(false);
  const [columns, setColumns] = useState([
    { key: "icd", label: "", type: "text" },
    { key: "owner", label: "", type: "text" },
    { key: "status", label: "", type: "choice",
      options: ["Not started", "In progress", "Complete"] },
    { key: "target", label: "", type: "date" },
    { key: "notes", label: "", type: "text" },
  ]);

  const chosen = keys.find((k) => k.key === keyField) ?? null;
  const named = columns.filter((c) => c.label.trim());
  const ready = Boolean(title.trim() && chosen && named.length);

  if (!keys.length) {
    return (
      <Modal title="Our own tracking" onClose={onClose} wide>
        <Notice kind="error">
          Nothing in this layer is filled in on every feature and different on
          every feature, so there is no way to line our rows up with theirs. A
          layer needs one such field, usually a code or a reference number.
        </Notice>
      </Modal>
    );
  }

  return (
    <Modal title="Our own tracking, against this layer" onClose={onClose} wide>
      <div style={{ display: "grid", gap: 14 }}>
        <Notice>
          This is ours. It lives in {APP_NAME}, it is editable by everybody on
          this contract, and it needs nobody's permission. The layer stays
          exactly as it is and is never written to.
        </Notice>

        <Labelled label="What to call it">
          <input style={inputStyle} value={title}
            onChange={(e) => setTitle(e.target.value)} />
        </Labelled>

        <Labelled label="Line our rows up on"
          hint="The field that names the same thing in both places. Prefer the program's own code over a row number: a GIS renumbers its rows when a layer is rebuilt, and that would unhook everything typed here.">
          <select style={inputStyle} value={keyField}
            onChange={(e) => setKeyField(e.target.value)}>
            {keys.map((k) => <option key={k.key} value={k.key}>{k.label}</option>)}
          </select>
        </Labelled>

        <div>
          <div style={{ fontSize: 12.5, color: T.soft, marginBottom: 10, lineHeight: 1.6 }}>
            The columns are yours and start unnamed, because what this program
            needs to record is not something the app should assume. Five are
            offered with types that suit interface control, an ICD number, an
            owner, a status, a target date and a note. Head them how you like,
            add more, remove what you do not want. A column with no heading is
            not created.
          </div>
          <ColumnsEditor columns={columns} onChange={setColumns} />
        </div>

        <div style={{ fontSize: 12.5, color: T.soft, lineHeight: 1.6 }}>
          It starts with {layer.rows.length} row
          {layer.rows.length === 1 ? "" : "s"}, one for each feature in the
          layer, already carrying {chosen ? chosen.label.toLowerCase() : "the key"}
          {labelCol ? ` and ${labelCol.label.toLowerCase()}` : ""}. Everything
          else starts empty.
        </div>

        <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
          <Btn kind="ghost" onClick={onClose}>Cancel</Btn>
          <Btn kind="gold" disabled={!ready || busy} onClick={async () => {
            setBusy(true);
            const taken = new Set(["school", "site", "name"]);
            const keyKey = keyFor(chosen.label, taken);
            const labelKey = labelCol ? keyFor(labelCol.label, taken) : null;
            const ok = await onCreate({
              title: title.trim(),
              keyField: chosen.key,
              ourKey: keyKey,
              columns: [
                { key: keyKey, label: chosen.label, type: "text", locked: true },
                ...(labelCol ? [{ key: labelKey, label: labelCol.label, type: "text", locked: true }] : []),
                ...named,
              ],
              rows: seedRows({
                layerRows: layer.rows, layerKey: chosen.key,
                ourKey: keyKey, labelKey: labelCol?.key, labelAs: labelKey,
              }),
            });
            setBusy(false);
            if (ok) onClose();
          }}>{busy ? "Building it..." : "Start it"}</Btn>
        </div>
      </div>
    </Modal>
  );
}

const inputStyle = {
  width: "100%", padding: "8px 10px", border: `1px solid ${T.line}`,
  borderRadius: 4, fontSize: 14, background: "#fff", color: T.ink,
};

/* The one impure thing on this screen, handed in so the visual preview can
   draw it against a saved copy of the layer. */
export default function LiveSource({
  source, contract, onBack, onSourceChange, read = fetchFeatures,
}) {
  const { loading, error, columns: layerColumns, rows: layerRows, placed, truncated, at, reload } =
    useLayer(source, read);
  const [gateKey, setGateKey] = useState(null);
  const [drill, setDrill] = useState(null);
  const [starting, setStarting] = useState(false);
  const [linkError, setLinkError] = useState("");

  /* Our own side of this layer: a tracker in this app, one row per feature,
     joined on a field that names the same thing in both places. It is ours
     outright, so it can be edited by anybody on the contract without asking
     the GIS for anything. */
  const { create } = useTrackers(contract?.id);
  const ours = useTracker(source.ourTrackerId ?? null);
  const ourTracker = source.ourTrackerId ? ours.tracker : null;

  const joined = useMemo(() => joinToLayer({
    layerRows: layerRows ?? [],
    layerKey: source.keyField,
    ourRows: ourTracker ? (ours.rows ?? []) : [],
    ourKey: source.ourKey,
  }), [layerRows, ours.rows, ourTracker, source.keyField, source.ourKey]);

  const rows = joined.rows;
  const columns = useMemo(() => (ourTracker
    ? [...(layerColumns ?? []), ...ourColumns(
        (ourTracker.columns ?? []).filter((c) => !c.locked))]
    : layerColumns), [layerColumns, ourTracker]);

  /* Saving the link puts it on the contract, beside the layer's address, so
     everybody assigned to the contract sees the same pairing. */
  const linkTracker = useCallback(async (patch) => {
    setLinkError("");
    const next = (contract?.map_sources ?? []).map((m) =>
      (m.id === source.id ? { ...m, ...patch } : m));
    const { error: saveError } = await supabase.from("contracts")
      .update({ map_sources: next }).eq("id", contract.id);
    if (saveError) { setLinkError(friendlyError(saveError)); return false; }
    await onSourceChange?.();
    return true;
  }, [contract, source.id, onSourceChange]);

  const gates = useMemo(
    () => (columns && rows ? findStatusColumns(columns, rows) : []),
    [columns, rows]);

  const isOurs = (key) => String(key ?? "").startsWith(OURS);

  const gate = gates.find((g) => g.key === gateKey) ?? gates[0] ?? null;

  /* Four states rather than two. A school moving through a review is not the
     same as a school nobody has started, and a field the client has not filled
     in is neither, so it is drawn as neither. */
  const paint = useMemo(() => (p) => {
    const word = stateLabel(p.status);
    const fill = statusFill(word);
    const ink = statusColor(word);
    if (!fill || fill === T.soft) return { fill: "#fff", ink: T.soft };
    return { fill, ink };
  }, []);

  const legend = useMemo(() => {
    if (!gate || !rows) return undefined;
    const order = ["Complete", "Completed", "Approved", "In progress", "Started",
                   "Pending", "Not started", "Not reported"];
    const present = [...new Set(rows.map((r) => stateLabel(r[gate.key])))]
      .sort((a, b) => order.indexOf(a) - order.indexOf(b));
    return present.map((label) => {
      const fill = statusFill(label);
      return fill && fill !== T.soft
        ? { label, fill, ink: statusColor(label) }
        : { label, fill: "#fff", ink: T.soft };
    });
  }, [gate, rows]);

  /* A review gate reads the same here as it does on the map. The layer
     writes a step nobody has reached yet as 0, which on its own looks like a
     count of nothing rather than a step not reported. */
  const gateKeys = useMemo(() => new Set(gates.map((g) => g.key)), [gates]);

  const drillColumns = useMemo(() => (columns ?? [])
    .filter((c) => !c.key.startsWith("_"))
    .map((c) => [
      c.label,
      gateKeys.has(c.key)
        ? (r) => stateLabel(r[c.key])
        : (r) => r[c.key] ?? "",
    ]), [columns, gateKeys]);

  const open = (title, records) => {
    if (!records?.length) return;
    setDrill({ title, rows: records, columns: drillColumns });
  };

  const interfaces = useMemo(
    () => (rows ? interfaceSummary(rows, "Risk") : null), [rows]);

  /* Every figure on this screen opens the records behind it, so each of these
     needs the actual rows and not just a count. Where a figure has a gap, the
     gap is what opens: which schools are missing coordinates, and which have
     no interfaces recorded, are the answers somebody can act on. The whole
     set opens when there is no gap. */
  const noCoords = useMemo(
    () => (rows ?? []).filter((r) => r[LAT_KEY] == null || r[LON_KEY] == null), [rows]);
  const onMap = useMemo(
    () => (rows ?? []).filter((r) => r[LAT_KEY] != null && r[LON_KEY] != null), [rows]);
  const noInterfaces = useMemo(
    () => (rows ?? []).filter((r) => parseInterfaceTags(r.Risk).length === 0), [rows]);

  const ranked = useMemo(() => {
    if (!columns || !rows) return null;
    const col = columns.find((c) => RANK_FIELD.test(c.label) || RANK_FIELD.test(c.key));
    if (!col) return null;
    const withRank = rows.filter((r) => typeof r[col.key] === "number");
    if (withRank.length < rows.length / 2) return null;
    return {
      col,
      rows: [...withRank].sort((a, b) => a[col.key] - b[col.key]),
      unranked: rows.filter((r) => typeof r[col.key] !== "number"),
    };
  }, [columns, rows]);

  const nameKey = useMemo(() => {
    if (!columns) return null;
    const named = columns.find((c) => /^(location|name|school|site|title)$/i.test(c.label));
    return (named ?? columns.find((c) => c.type === "text"))?.key ?? null;
  }, [columns]);
  const nameOf = (r) => String(r?.[nameKey] ?? "This record");

  const header = (
    <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
      <Btn small kind="ghost" onClick={reload} disabled={loading}>
        <RefreshCw size={13} style={{ verticalAlign: "-2px" }} /> {loading ? "Reading" : "Refresh"}
      </Btn>
      <a href={queryUrl(source.url, source.where || "1=1") ?? source.url}
        target="_blank" rel="noreferrer"
        style={{ fontSize: 12, color: T.steel, textDecoration: "none", whiteSpace: "nowrap" }}>
        The layer <ExternalLink size={11} style={{ verticalAlign: "-1px" }} />
      </a>
      {onBack && (
        <Btn small kind="ghost" onClick={onBack}>
          <ArrowLeft size={13} style={{ verticalAlign: "-2px" }} /> All trackers
        </Btn>
      )}
    </div>
  );

  if (error) {
    return (
      <Panel title={source.title} right={header}>
        <Notice kind="error">{error}</Notice>
        <div style={{ fontSize: 13, color: T.soft, marginTop: 12, lineHeight: 1.7 }}>
          Nothing has been lost. This screen holds no copy of the layer, it reads
          it every time, so the moment the layer answers again the maps are back.
          If it keeps refusing, the usual cause is that the layer has been made
          private, in which case whoever administers it can share it again.
        </div>
      </Panel>
    );
  }

  if (loading && !layerRows) return <Empty text="Reading the layer..." />;
  if (!layerRows?.length) {
    return (
      <Panel title={source.title} right={header}>
        <Empty text="The layer answered, with no features in it. If this source has a filter on it, that filter may be excluding everything." />
      </Panel>
    );
  }

  const hasMap = Boolean(findCoordinates(columns, rows));
  const gateTally = gate
    ? rows.reduce((acc, r) => {
        const k = stateLabel(r[gate.key]);
        acc[k] = (acc[k] ?? 0) + 1;
        return acc;
      }, {})
    : {};

  return (
    <div style={{ display: "grid", gap: 16, gridTemplateColumns: "minmax(0, 1fr)" }}>
      {drill && <DrillPanel drill={drill} onClose={() => setDrill(null)} />}

      <Panel title={source.title} right={header}>
        <div style={{ display: "grid", gap: 12,
                      gridTemplateColumns: "repeat(auto-fit, minmax(170px, 1fr))" }}>
          <div style={{ cursor: "pointer" }} onClick={() => open("Every record", rows)}>
            <Kpi label="In scope" value={rows.length} sub="Click to open them all" />
          </div>
          <div style={{ cursor: "pointer" }}
            onClick={() => (noCoords.length
              ? open("Schools with no coordinates", noCoords)
              : open("Schools on the map", onMap))}>
            <Kpi label="On the map" value={placed} color={T.steel}
              sub={noCoords.length
                ? `Click to see the ${noCoords.length} without coordinates`
                : "All of them. Click to open them"} />
          </div>
          {interfaces && interfaces.categories.length > 0 && (
            <div style={{ cursor: "pointer" }}
              onClick={() => (noInterfaces.length
                ? open("Schools with no interfaces recorded", noInterfaces)
                : open("Every record", rows))}>
              <Kpi label="Interface types" value={interfaces.categories.length} color={GOLD_INK}
                sub={noInterfaces.length
                  ? `Click to see the ${noInterfaces.length} of ${rows.length} with none recorded`
                  : `Recorded across all ${rows.length}`} />
            </div>
          )}
          {/* Not a set of records, so nothing to open. It is the moment this
              screen last asked ArcGIS, which is the thing worth knowing about
              a number read live rather than stored. */}
          <Kpi label="Read at" value={at ? at.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }) : "-"}
            sub="Live from ArcGIS" />
        </div>
        {truncated && (
          <div style={{ marginTop: 12 }}>
            <Notice kind="error">
              This layer holds more than this screen will draw, so what you are
              looking at is the first {layerRows.length.toLocaleString()} of it
              and not all of it. Narrow it with a filter on the contract's
              settings, by island or by status, and it becomes a map you can
              trust again.
            </Notice>
          </div>
        )}
        <div style={{ fontSize: 12.5, color: T.soft, marginTop: 12, lineHeight: 1.7 }}>
          {source.description ? `${source.description} ` : ""}
          Read from ArcGIS as it stands. Nothing here can be changed from this
          app, because the GIS is where the program keeps this and a second
          answer would only ever be a wrong one. Edit it in ArcGIS and press
          Refresh, and this follows.
        </div>
      </Panel>

      {hasMap && (
        <>
          {gates.length > 1 && (
            <Panel title="Colour the map by" accent={T.steel}>
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 10 }}>
                {gates.map((g) => (
                  <button key={g.key} onClick={() => setGateKey(g.key)}
                    title={isOurs(g.key) ? "Ours, kept in this app" : "From the layer"}
                    style={{
                      border: `1px solid ${g.key === gate?.key ? T.navy
                        : isOurs(g.key) ? GOLD_INK : T.line}`,
                      background: g.key === gate?.key ? T.navy : T.panel,
                      color: g.key === gate?.key ? "#fff" : isOurs(g.key) ? GOLD_INK : T.ink,
                      borderRadius: 4, padding: "5px 11px", fontSize: 12.5,
                      fontWeight: 700, cursor: "pointer",
                    }}>{isOurs(g.key) ? `${g.label} (ours)` : g.label}</button>
                ))}
              </div>
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                {Object.entries(gateTally)
                  .sort((a, b) => b[1] - a[1])
                  .map(([word, count]) => (
                    <span key={word} style={{ cursor: "pointer" }}
                      onClick={() => open(`${gate.label}: ${word}`,
                        rows.filter((r) => stateLabel(r[gate.key]) === word))}>
                      <Chip color={statusColor(word)}>{count} {word.toLowerCase()}</Chip>
                    </span>
                  ))}
              </div>
              <div style={{ fontSize: 12, color: T.soft, marginTop: 10, lineHeight: 1.6 }}>
                Each of these is a step a school passes through. Pick one and the
                islands fill in as the client records it. A step nobody has
                entered yet reads as not reported, which is not the same as not
                started and is not counted as progress either way.
              </div>
            </Panel>
          )}

          <SiteMap columns={columns} rows={rows}
            title={`${source.title}: where they are`}
            statusKey={gate?.key}
            colorOf={paint}
            legend={legend}
            note={gate ? `Coloured by ${gate.label}.` : undefined}
            onDrill={(d) => setDrill({ ...d, columns: drillColumns })} />
        </>
      )}

      {interfaces && interfaces.categories.length > 0 && (
        <div style={{ display: "grid", gap: 16, alignItems: "start",
                      gridTemplateColumns: "repeat(auto-fit, minmax(320px, 1fr))" }}>
          <Panel title="Interface types across the program" accent={T.gold}>
            <div style={{ fontSize: 12.5, color: T.soft, marginBottom: 12, lineHeight: 1.6 }}>
              Counted in schools, not in tags. A school with drainage flagged
              twice has one drainage problem here.
            </div>
            {interfaces.categories.map((c) => (
              <CountBar key={c.code} label={c.label} count={c.count}
                of={interfaces.categories[0].count}
                color={T.steel} one="school" many="schools"
                onOpen={() => open(c.label, c.schools)} />
            ))}
            {interfaces.untagged > 0 && (
              <div style={{ fontSize: 12, color: T.soft, marginTop: 12 }}>
                {interfaces.untagged} with no interface recorded.
              </div>
            )}
          </Panel>

          <Panel title="By family" accent={T.gold}>
            {interfaces.families.map((f) => (
              <CountBar key={f.code} label={FAMILY_LABEL[f.code] ?? f.code} count={f.count}
                of={interfaces.families[0].count}
                color={T.navy} one="school" many="schools"
                onOpen={() => open(FAMILY_LABEL[f.code] ?? f.code, f.schools)} />
            ))}
            <div style={{ fontSize: 12, color: T.soft, marginTop: 12, lineHeight: 1.6 }}>
              The five families in the team's Interface Control Document. Wording
              is the ICD's, grouping is the GIS's, and where the two disagree the
              GIS is what you see, because it is the copy people work in.
            </div>
          </Panel>
        </div>
      )}

      {source.photoUrl && layerColumns && (
        <Photographs url={source.photoUrl} read={read}
          layerColumns={layerColumns} layerRows={layerRows} labelKey={nameKey} />
      )}

      {/* Ours. Editable in place, live between everybody on the contract, and
          dependent on nobody's permission but our own. */}
      <Panel title={ourTracker ? ourTracker.title : "Our own tracking"} accent={GOLD_INK}
        right={ourTracker ? (
          <Chip color={GOLD_INK}>
            {joined.matched} of {layerRows.length} lined up
          </Chip>
        ) : undefined}>
        {linkError && <div style={{ marginBottom: 10 }}><Notice kind="error">{linkError}</Notice></div>}
        {ours.error && <div style={{ marginBottom: 10 }}><Notice kind="error">{ours.error}</Notice></div>}

        {!ourTracker ? (
          <div>
            <div style={{ fontSize: 13, color: T.soft, lineHeight: 1.7, marginBottom: 14 }}>
              The layer above is read only, and what we need to record against a
              school has no field in it anyway. So this is the other half: a
              tracker of our own, one row per {source.thing || "feature"}, with
              whatever columns you decide. ICD number, owner, target date, what
              was agreed on Tuesday. It is editable in place, everybody on this
              contract sees each other's changes as they happen, and it needs
              nobody's rights to anything.
            </div>
            <Btn kind="gold" onClick={() => setStarting(true)}>Start our own tracker</Btn>
          </div>
        ) : (
          <>
            <div style={{ fontSize: 12.5, color: T.soft, marginBottom: 10, lineHeight: 1.6 }}>
              Click any value to change it. It saves as you leave the cell and
              everyone else in this contract sees it as it happens. The first
              column or two are the join and are left alone.
            </div>
            <RowTable
              columns={ourTracker.columns ?? []}
              rows={ours.rows ?? []}
              onEdit={ours.setCell} />
            {joined.orphans.length > 0 && (
              <div style={{ marginTop: 12 }}>
                <Notice kind="error">
                  {joined.orphans.length} row
                  {joined.orphans.length === 1 ? "" : "s"} here match nothing in
                  the layer any more, so they are not on the map. Usually that
                  means the layer renamed something. They are still listed above
                  and nothing typed into them has been lost.
                </Notice>
              </div>
            )}
          </>
        )}
      </Panel>

      <Panel title="Everything the layer holds">
        <div style={{ fontSize: 12.5, color: T.soft, marginBottom: 10, lineHeight: 1.6 }}>
          Every field, exactly as ArcGIS records it. Read only, and filtered with
          the box below.
        </div>
        <RowTable columns={(layerColumns ?? []).filter((c) => !c.key.startsWith("_"))}
          rows={layerRows} />
      </Panel>

      {starting && (
        <StartOurTracker
          layer={{ columns: layerColumns, rows: layerRows }}
          source={source}
          onClose={() => setStarting(false)}
          onCreate={async ({ title, keyField, ourKey, columns: cols, rows: seeded }) => {
            const id = await create({ title, columns: cols, rows: seeded });
            if (!id) { setLinkError("The tracker could not be created."); return false; }
            return linkTracker({ ourTrackerId: id, keyField, ourKey });
          }} />
      )}

      {ranked && (
        <Panel title="In priority order" accent={T.steel}>
          <div style={{ fontSize: 12.5, color: T.soft, marginBottom: 12 }}>
            Straight off the layer's own ranking. Click any row to open it.
          </div>
          <div style={{ display: "grid", gap: 2 }}>
            {ranked.rows.map((r) => (
              <div key={r._id} onClick={() => open(nameOf(r), [r])}
                style={{ display: "flex", gap: 12, alignItems: "center", cursor: "pointer",
                         padding: "5px 8px", borderBottom: `1px solid ${T.line}` }}>
                <span style={{ width: 26, fontWeight: 700, color: T.navy, fontSize: 13 }}>
                  {r[ranked.col.key]}
                </span>
                <span style={{ flex: 1, minWidth: 0, fontSize: 13, overflow: "hidden",
                               textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                  {nameOf(r)}
                </span>
                {gate && (
                  <Chip color={statusColor(stateLabel(r[gate.key]))}>
                    {stateLabel(r[gate.key])}
                  </Chip>
                )}
              </div>
            ))}
          </div>
          {ranked.unranked.length > 0 && (
            <div style={{ fontSize: 12, color: T.soft, marginTop: 10 }}>
              {ranked.unranked.map(nameOf).join(", ")} carr
              {ranked.unranked.length === 1 ? "ies" : "y"} no ranking in the layer.
            </div>
          )}
        </Panel>
      )}
    </div>
  );
}
